import React, { type ComponentProps, type PropsWithChildren } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { StyleSheet, View, type ViewProps } from 'react-native';
import Animated, { type AnimatedProps } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarContainer = (props: Props) => {
  const { children, entering, isLiquidGlassEnabled = false } = props;

  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();

  // ─── Styles ──────────────────────────────────────────────────────────

  const contentDynamicStyles = useStyle(() => {
    const paddingBottom = Math.max(safeAreaBottomInset, theme.spacing['6']);

    return { paddingBottom };
  }, [safeAreaBottomInset]);

  const contentStyles = [styles.content, contentDynamicStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View entering={entering} style={styles.container}>
      {isLiquidGlassEnabled ? null : (
        <LinearGradient
          colors={NAVBAR_GRADIENT_COLORS}
          locations={NAVBAR_GRADIENT_LOCATIONS}
          start={NAVBAR_GRADIENT_START}
          end={NAVBAR_GRADIENT_END}
          style={styles.gradientContainer}
        />
      )}

      <View style={contentStyles}>{children}</View>
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const NAVBAR_GRADIENT_COLORS: GradientColors = [
  'rgba(248, 247, 240, 0)',
  'rgba(248, 247, 240, 0.7)',
];

const NAVBAR_GRADIENT_LOCATIONS: GradientLocations = [0, 0.5];
const NAVBAR_GRADIENT_START = { x: 0, y: 0 };
const NAVBAR_GRADIENT_END = { x: 0, y: 1 };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    paddingHorizontal: theme.spacing['4'],
    pointerEvents: 'box-none',
  },
  gradientContainer: {
    ...StyleSheet.absoluteFill,
    minHeight: 300,
    pointerEvents: 'none',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['2'],
    pointerEvents: 'box-none',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  entering?: AnimatedProps<ViewProps>['entering'];
  isLiquidGlassEnabled?: boolean;
}>;
type GradientColors = ComponentProps<typeof LinearGradient>['colors'];
type GradientLocations = ComponentProps<typeof LinearGradient>['locations'];
